import { useEffect, useRef } from 'react'
import { useParams, useLocation, useNavigate } from 'react-router-dom'
import { resolveTreeRoute } from '../services'
import { normalizePathname } from '../utils/cantonUrl'
import * as RT from '../config/routeType'

/**
 * Valida la URL pública de un árbol por código contra la ruta canónica del backend.
 * La apertura del popup queda a cargo de useTreeNavigation cuando el GeoJSON correcto
 * del cantón ya está cargado.
 */
export const useTreeFromURL = () => {
  const params = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const resolvedRouteRef = useRef(null)

  const treeId = params.treeId || params.codeTree
  const isPublicTreeRoute = Boolean(
    params.iso2 &&
      params.canton &&
      treeId &&
      !location.pathname.includes(RT.MANAGEMENT_TREE) &&
      !location.pathname.includes(RT.REGISTER_TREE),
  )

  useEffect(() => {
    if (!isPublicTreeRoute) {
      resolvedRouteRef.current = null
      return
    }

    const routeKey = `${treeId}:${normalizePathname(location.pathname)}`
    if (resolvedRouteRef.current === routeKey) return

    let cancelled = false

    const resolveRoute = async () => {
      try {
        const result = await resolveTreeRoute(treeId)
        if (cancelled) return

        if (!result?.found || !result.paths?.public) {
          resolvedRouteRef.current = routeKey
          return
        }

        const currentPath = normalizePathname(location.pathname)
        const canonicalPath = normalizePathname(result.paths.public)

        if (canonicalPath !== currentPath) {
          // Pre-cachear el routeKey canónico para que el efecto post-redirect no relance resolve.
          // No cerrar el popup: la redirección es normalización de URL, no un cambio de árbol.
          resolvedRouteRef.current = `${treeId}:${canonicalPath}`
          navigate(canonicalPath, { replace: true })
          return
        }

        resolvedRouteRef.current = routeKey
      } catch (error) {
        if (!cancelled) {
          console.error('[useTreeFromURL] Error al resolver ruta del árbol:', error)
          resolvedRouteRef.current = null
        }
      }
    }

    resolveRoute()

    return () => {
      cancelled = true
    }
  }, [
    isPublicTreeRoute,
    location.pathname,
    navigate,
    treeId,
  ])
}
